import {CommonModule} from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    inject,
    Injector,
    input,
    NgZone,
    OnDestroy,
    OnInit,
    signal,
    ViewChild
} from '@angular/core';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {MatChipInputEvent} from '@angular/material/chips';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {MaterialModule} from '../../../material.module';
import {ConnectProviderDef, ConnectProvidersService} from '../../../services/connect/connect-providers.service';
import {
    ConnectRun,
    ConnectRunContext,
    ConnectRunReplaySummary,
    ConnectWorkflow,
    ConnectWorkflowCanvasBackground,
    ConnectWorkflowCanvasBackgroundStyle,
    ConnectWorkflowEdge,
    ConnectWorkflowNode,
    ConnectWorkflowsService
} from '../../../services/connect/connect-workflows.service';
import {
    Edge,
    initializeModel,
    type ModelAdapter,
    NgDiagramBackgroundComponent,
    NgDiagramComponent,
    NgDiagramConfig,
    NgDiagramNodeResizeAdornmentComponent,
    NgDiagramNodeTemplate,
    NgDiagramNodeTemplateMap,
    NgDiagramPortComponent,
    NgDiagramSelectionService,
    type NodeResizedEvent,
    PaletteItemDroppedEvent,
    type Point,
    provideNgDiagram,
    SelectionChangedEvent,
    type SelectionMovedEvent,
    SelectionRemovedEvent,
    type SimpleNode,
} from 'ng-diagram';
import {of, Subscription, timeout, timer} from 'rxjs';
import {catchError, finalize, switchMap, take, takeWhile} from 'rxjs/operators';
import {ToastService} from '../../../services/layout/toast.service';
import {ConnectWorkflowNodePaletteComponent} from './connect-workflow-node-palette.component';
import {ConnectWorkflowDebugPanelComponent} from './connect-workflow-debug-panel.component';
import {ConnectWorkflowInspectorComponent} from './connect-workflow-inspector.component';
import {ConnectWorkflowCanvasInteractionsService} from './connect-workflow-canvas-interactions.service';

type ConnectDiagramNodeData = {
  label: string;
  ref?: string;
  type: string;
  status?: string;
  workflowId?: string;
  providerId?: string;
  providerName?: string;
  providerLogo?: string;
  providerPalette?: ConnectProviderDef['palette'];
};

type ConnectDiagramBackgroundData = {
    label?: string;
    fill?: string;
    stroke?: string;
    opacity?: number;
    radius?: number;
    locked?: boolean;
};

function providerFromRef(ref: unknown): string {
  if (!ref) return '';
  try {
    const s = String(ref).trim();
    if (!s) return '';
    const i = s.indexOf('.');
    return (i > 0 ? s.slice(0, i) : s).toLowerCase();
  } catch {
    return '';
  }
}

type BackgroundPreset = 'retrieval' | 'processing' | 'storage';

const BACKGROUND_PRESETS: Record<BackgroundPreset, {
    label: string;
    style: Required<Pick<ConnectWorkflowCanvasBackgroundStyle, 'fill' | 'stroke' | 'opacity' | 'radius'>>
}> =
    {
        retrieval: {
            label: 'Retrieval',
            style: {fill: '#fee2e2', stroke: '#fecaca', opacity: 0.35, radius: 18},
        },
        processing: {
            label: 'Processing',
            style: {fill: '#dcfce7', stroke: '#bbf7d0', opacity: 0.28, radius: 18},
        },
        storage: {
            label: 'Storage',
            style: {fill: '#fef9c3', stroke: '#fde68a', opacity: 0.3, radius: 18},
        },
    };

@Component({
    selector: 'app-connect-diagram-background',
    standalone: true,
    imports: [CommonModule, NgDiagramNodeResizeAdornmentComponent],
    template: `
    <ng-diagram-node-resize-adornment [defaultResizable]="!node().data.locked">
      <div class="diagram-background" [class.is-locked]="!!node().data.locked" [ngStyle]="styleVars(node().data)">
        @if (node().data.label) {
          <div class="diagram-background__label">{{ node().data.label }}</div>
        }
      </div>
    </ng-diagram-node-resize-adornment>
  `,
    styles: [
        `
      .diagram-background {
        position: relative;
        width: 100%;
        height: 100%;
        border-radius: var(--connect-bg-radius, 18px);
        border: 2px dashed var(--connect-bg-stroke, rgba(15, 23, 42, 0.14));
        overflow: hidden;
        box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
        cursor: grab;
      }
      .diagram-background.is-locked {
        cursor: default;
        pointer-events: none;
      }
      .diagram-background::before {
        content: '';
        position: absolute;
        inset: 0;
        background: var(--connect-bg-fill, #e2e8f0);
        opacity: var(--connect-bg-opacity, 0.25);
      }
      .diagram-background__label {
        position: absolute;
        top: 10px;
        left: 10px;
        padding: 4px 10px;
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.88);
        border: 1px solid rgba(15, 23, 42, 0.12);
        color: rgba(15, 23, 42, 0.82);
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.02em;
        user-select: none;
        pointer-events: none;
      }
    `,
    ],
})
export class ConnectDiagramBackgroundComponent implements NgDiagramNodeTemplate {
    node = input.required<SimpleNode<ConnectDiagramBackgroundData>>();

    styleVars(data: ConnectDiagramBackgroundData): Record<string, string> {
        const fill = typeof data.fill === 'string' && data.fill.trim() ? data.fill.trim() : '#e2e8f0';
        const stroke = typeof data.stroke === 'string' && data.stroke.trim() ? data.stroke.trim() : 'rgba(15, 23, 42, 0.14)';
        const opacity = typeof data.opacity === 'number' && isFinite(data.opacity) ? Math.min(Math.max(data.opacity, 0), 1) : 0.25;
        const radius = typeof data.radius === 'number' && isFinite(data.radius) ? Math.max(0, data.radius) : 18;

        return {
            '--connect-bg-fill': fill,
            '--connect-bg-stroke': stroke,
            '--connect-bg-opacity': String(opacity),
            '--connect-bg-radius': `${radius}px`,
        };
    }
}

@Component({
  selector: 'app-connect-diagram-node',
  standalone: true,
  imports: [CommonModule, NgDiagramPortComponent],
  template: `
      <div class="diagram-node" (dblclick)="openFullscreen($event)" [ngClass]="variantClass(node().data)"
           [ngStyle]="paletteStyle(node().data.providerPalette)">
      @if (isSucceeded(node().data.status)) {
        <div class="diagram-node__complete" aria-label="node-complete" title="Completed">✓</div>
      }
    
      <div class="diagram-node__row">
        @if (node().data.providerLogo) {
          <img
            class="diagram-node__logo"
            [src]="node().data.providerLogo"
            [alt]="(node().data.providerName || node().data.label || 'Provider') + ' logo'"
            loading="lazy"
            />
        } @else {
          <div class="diagram-node__icon" aria-hidden="true">{{ glyphForType(node().data.type) }}</div>
        }
    
        <div class="diagram-node__label">{{ node().data.label || node().id }}</div>
        @if (node().data.status) {
          <span class="diagram-node__status" [ngClass]="statusBadgeClass(node().data.status)">{{ node().data.status }}</span>
        }
      </div>
    
      @if (node().data.ref) {
        <p class="diagram-node__ref">{{ node().data.ref }}</p>
      }
      @if (node().data.type) {
        <p class="diagram-node__kind">{{ node().data.type }}</p>
      }
    
      @if (isManualTrigger(node().data.type)) {
        <div class="diagram-node__actions">
          <button
            type="button"
            class="diagram-node__run"
            (mousedown)="$event.stopPropagation()"
            (click)="runWorkflow($event)"
            [disabled]="running() || !node().data.workflowId"
            aria-label="Run workflow"
            [attr.title]="node().data.workflowId ? 'Run this workflow' : 'Save this workflow to enable running'"
            >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M8 5v14l11-7z"></path>
            </svg>
            <span>Run</span>
          </button>
        </div>
      }
      <ng-diagram-port id="in" type="target" side="left"></ng-diagram-port>
      <ng-diagram-port id="out" type="source" side="right"></ng-diagram-port>
    </div>
    `,
  styles: [
    `
      .diagram-node {
        position: relative;
        border: 2px solid #c4b5fd;
        background: #f5f3ff;
        border-radius: 16px;
        min-width: 180px;
        padding: 14px;
        box-shadow: 0 14px 30px rgba(15, 23, 42, 0.12);
        transition: box-shadow 120ms ease, transform 120ms ease;
        user-select: none;
      }
      .diagram-node:hover {
        box-shadow: 0 18px 38px rgba(15, 23, 42, 0.16);
      }
      .diagram-node.variant-blue {
        border-color: #c4b5fd;
        background: #f5f3ff;
      }
      .diagram-node.variant-green {
        border-color: #6ee7b7;
        background: #ecfdf5;
      }
      .diagram-node.variant-orange {
        border-color: #fcd34d;
        background: #fffbeb;
      }
      .diagram-node.variant-purple {
        border-color: #f0abfc;
        background: #fdf4ff;
      }
      .diagram-node.variant-red {
        border-color: #fda4af;
        background: #fff1f2;
      }
      .diagram-node__complete {
        position: absolute;
        top: -6px;
        left: -6px;
        width: 18px;
        height: 18px;
        border-radius: 999px;
        background: #16a34a;
        border: 2px solid #ffffff;
        color: #ffffff;
        font-size: 11px;
        font-weight: 800;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .diagram-node__row {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .diagram-node__logo {
        width: 20px;
        height: 20px;
        border-radius: 4px;
        border: 1px solid rgba(255, 255, 255, 0.75);
        background: rgba(255, 255, 255, 0.9);
        object-fit: contain;
        box-shadow: 0 6px 14px rgba(15, 23, 42, 0.12);
      }
      .diagram-node__icon {
        width: 20px;
        height: 20px;
        border-radius: 6px;
        border: 1px solid rgba(15, 23, 42, 0.08);
        background: rgba(255, 255, 255, 0.7);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 10px;
        font-weight: 800;
        color: rgba(15, 23, 42, 0.7);
      }
      .diagram-node__label {
        font-weight: 700;
        font-size: 13px;
        line-height: 1.2;
      }
      .diagram-node__status {
        margin-left: auto;
        padding: 3px 10px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 800;
        color: #ffffff;
        text-transform: lowercase;
        box-shadow: 0 8px 14px rgba(15, 23, 42, 0.1);
      }
      .diagram-node__status.status--queued {
        background: #64748b;
        position: relative;
        overflow: hidden;
      }

      .diagram-node__status.status--queued::after {
        content: '';
        position: absolute;
        top: 0;
        left: -35%;
        width: 35%;
        height: 100%;
        background: rgba(255, 255, 255, 0.26);
        transform: skewX(-20deg);
        animation: connectDiagramQueuedShimmer 1.35s ease-in-out infinite;
      }
      .diagram-node__status.status--running {
        background: #f59e0b;
        position: relative;
        padding-left: 24px;
      }

      .diagram-node__status.status--running::before {
        content: '';
        position: absolute;
        left: 8px;
        top: 50%;
        width: 10px;
        height: 10px;
        border-radius: 999px;
        border: 2px solid rgba(255, 255, 255, 0.75);
        border-top-color: rgba(255, 255, 255, 0.25);
        transform: translateY(-50%) rotate(0deg);
        animation: connectDiagramSpin 900ms linear infinite;
      }
      .diagram-node__status.status--succeeded {
        background: #059669;
      }
      .diagram-node__status.status--failed {
        background: #e11d48;
      }
      .diagram-node__status.status--cancelled,
      .diagram-node__status.status--timed_out {
        background: #f97316;
      }
      .diagram-node__ref {
        margin: 6px 0 0;
        font-size: 12px;
        opacity: 0.82;
        word-break: break-word;
      }
      .diagram-node__kind {
        margin: 3px 0 0;
        font-size: 11px;
        opacity: 0.7;
        word-break: break-word;
      }
      .diagram-node__actions {
        margin-top: 10px;
      }
      .diagram-node__run {
        width: 100%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        border-radius: 12px;
        padding: 8px 10px;
        border: 1px solid #047857;
        background: #059669;
        color: #ffffff;
        font-weight: 700;
        cursor: pointer;
        user-select: none;
        transition: background-color 120ms ease, border-color 120ms ease, opacity 120ms ease;
      }
      .diagram-node__run:hover:not(:disabled) {
        background: #047857;
        border-color: #047857;
      }
      .diagram-node__run:disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }
      ng-diagram-port {
        position: absolute;
      }
      ng-diagram-port[side='left'] {
        top: 50%;
        left: -8px;
        transform: translateY(-50%);
      }
      ng-diagram-port[side='right'] {
        top: 50%;
        right: -8px;
        transform: translateY(-50%);
      }

      @keyframes connectDiagramSpin {
        to {
          transform: translateY(-50%) rotate(360deg);
        }
      }

      @keyframes connectDiagramQueuedShimmer {
        0% {
          left: -35%;
          opacity: 0;
        }
        25% {
          opacity: 0.85;
        }
        100% {
          left: 120%;
          opacity: 0;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .diagram-node__status.status--queued::after,
        .diagram-node__status.status--running::before {
          animation: none;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConnectDiagramNodeComponent implements NgDiagramNodeTemplate<ConnectDiagramNodeData> {
  private readonly workflowsService = inject(ConnectWorkflowsService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
    private readonly interactions = inject(ConnectWorkflowCanvasInteractionsService);

  node = input.required<SimpleNode<ConnectDiagramNodeData>>();
  running = signal<boolean>(false);

    openFullscreen(event: MouseEvent): void {
        const target = event.target as HTMLElement | null;
        if (target?.closest('button, a, input, textarea, select, [role="button"]')) return;

        event.preventDefault();
        event.stopPropagation();
        this.interactions.requestNodeFullscreen(this.node().id);
    }

  isManualTrigger(type?: string): boolean {
    const normalized = String(type || '').trim();
    return normalized === 'trigger.manual' || normalized === 'manual_trigger';
  }

  isSucceeded(status?: string): boolean {
    const s = String(status || '').toLowerCase();
    return s === 'succeeded' || s === 'success';
  }

  statusBadgeClass(status?: string): string {
    const s = String(status || '').toLowerCase();
    if (!s) return '';
    if (s === 'queued') return 'status--queued';
    if (s === 'running') return 'status--running';
    if (s === 'succeeded' || s === 'success') return 'status--succeeded';
    if (s === 'failed' || s.includes('fail')) return 'status--failed';
    if (s === 'cancelled') return 'status--cancelled';
    if (s === 'timed_out' || s === 'timeout') return 'status--timed_out';
    if (s.includes('queue')) return 'status--queued';
    if (s.includes('run')) return 'status--running';
    return '';
  }

  paletteStyle(palette?: ConnectProviderDef['palette']): Record<string, string> {
    const style: Record<string, string> = {};
    if (palette?.background) style['background-color'] = palette.background;
    if (palette?.border) style['border-color'] = palette.border;
    if (palette?.text) style['color'] = palette.text;
    return style;
  }

  variantClass(data: ConnectDiagramNodeData): string {
    const paletteColor = String(data.providerPalette?.color || '').toLowerCase();
    const color = paletteColor || this.defaultColorForType(data.type);
    if (color === 'green') return 'variant-green';
    if (color === 'orange') return 'variant-orange';
    if (color === 'purple') return 'variant-purple';
    if (color === 'red') return 'variant-red';
    return 'variant-blue';
  }

  private defaultColorForType(type?: string): string {
    const t = String(type || '').toLowerCase();
    if (t.startsWith('trigger.')) return 'green';
    if (t === 'api.http') return 'red';
    if (t === 'api.connector' || t === 'action') return 'blue';
    if (t.startsWith('ai.')) return 'purple';
    if (t === 'control.condition') return 'orange';
    if (t === 'control.wait') return 'orange';
    if (t === 'control.loop') return 'purple';
    if (t === 'control.transform') return 'purple';
    if (t === 'control.join') return 'green';
    if (t === 'control.parallel') return 'blue';
    return 'blue';
  }

  glyphForType(type?: string): string {
    const t = String(type || '').toLowerCase();
    if (t.startsWith('trigger.')) return 'T';
    if (t.startsWith('api.')) return 'A';
    if (t.startsWith('ai.')) return 'AI';
    if (t.startsWith('control.')) return 'C';
    return 'N';
  }

  runWorkflow(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.running()) return;

    const workflowId = typeof (this.node().data as any)?.workflowId === 'string' ? String((this.node().data as any).workflowId).trim() : '';
    if (!workflowId) {
      this.toastService.showError('Workflow must be saved to run.', 3500);
      return;
    }

    this.running.set(true);
    this.toastService.showInfo('Starting run...', 1200);

    this.workflowsService
      .runWorkflow(workflowId)
      .pipe(
        take(1),
        catchError(err => {
          const message = (err as {message?: string} | null)?.message || 'Failed to start run.';
          this.toastService.showError(`Run failed: ${message}`, 5000);
          return of(null);
        }),
        finalize(() => this.running.set(false)),
      )
      .subscribe(run => {
        if (!run) return;
        const runId = String((run as any).runId || '').trim();
        if (!runId) {
          this.toastService.showSuccess('Run queued.', 3500);
          return;
        }
        this.toastService.showSuccess(`Run queued: ${runId}`, 4000);
        this.router.navigate(['/connect/workflows', workflowId, 'run', runId]);
      });
  }
}

@Component({
  selector: 'app-connect-workflow-canvas',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MaterialModule,
    NgDiagramComponent,
    NgDiagramBackgroundComponent,
    ConnectWorkflowNodePaletteComponent,
    ConnectWorkflowDebugPanelComponent,
    ConnectWorkflowInspectorComponent,
  ],
  templateUrl: './connect-workflow-canvas.component.html',
  styleUrls: ['../connect-shared.scss', './connect-workflow-canvas.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {class: 'connect-theme'},
  providers: [provideNgDiagram()],
})
export class ConnectWorkflowCanvasComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly workflowsService = inject(ConnectWorkflowsService);
  private readonly providersService = inject(ConnectProvidersService);
    readonly workflowSettingsOpen = signal<boolean>(false);
    private readonly interactions = inject(ConnectWorkflowCanvasInteractionsService);
  private readonly injector = inject(Injector);
  private readonly zone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);
  private sub = new Subscription();
  private loadSub: Subscription | null = null;
  private lastRequestedWorkflowId: string | null = null;

  workflow = signal<ConnectWorkflow | null>(null);
  nodes = signal<ConnectWorkflowNode[]>([]);
  edges = signal<ConnectWorkflowEdge[]>([]);
    backgrounds = signal<ConnectWorkflowCanvasBackground[]>([]);
  loading = signal<boolean>(false);
  saving = signal<boolean>(false);
  error = signal<string | null>(null);
  selectedNodeId = signal<string | null>(null);
  selectedEdgeId = signal<string | null>(null);
    selectedBackgroundId = signal<string | null>(null);
  visualizeRunId = signal<string | null>(null);
  visualizedRun = signal<ConnectRun | null>(null);
  visualizedRunLoading = signal<boolean>(false);
  visualizedRunError = signal<string | null>(null);
  visualizedRunContext = signal<ConnectRunContext | null>(null);
  visualizedRunContextLoading = signal<boolean>(false);
  visualizedRunContextError = signal<string | null>(null);
  visualizedRunReplay = signal<ConnectRunReplaySummary | null>(null);
  visualizedRunReplayLoading = signal<boolean>(false);
  visualizedRunReplayError = signal<string | null>(null);
    readonly tagSeparatorKeysCodes: number[] = [ENTER, COMMA];
    private readonly selectionService = inject(NgDiagramSelectionService);

    readonly selectedBackground = computed(() => {
        const id = this.selectedBackgroundId();
        if (!id) return null;
        return this.backgrounds().find(b => b.id === id) ?? null;
    });

  providers = signal<ConnectProviderDef[]>([]);
  providersLoading = signal<boolean>(false);
  readonly providerMeta = computed(() => {
    const map = new Map<string, ConnectProviderDef>();
    for (const p of this.providers()) {
      map.set(p.id, p);
    }
    return map;
  });

  private runSub: Subscription | null = null;
  private runContextSub: Subscription | null = null;
  private runReplaySub: Subscription | null = null;
  readonly diagramModel: ModelAdapter = initializeModel({nodes: [], edges: []}, this.injector);
    readonly nodeTemplateMap = new NgDiagramNodeTemplateMap([
        ['connect.background', ConnectDiagramBackgroundComponent],
        ['connect.default', ConnectDiagramNodeComponent],
    ]);
  readonly hasAnyTriggerNodes = computed(() => this.nodes().some(n => String(n.type ?? '').toLowerCase().startsWith('trigger.')));

  readonly diagramConfig: NgDiagramConfig = {
    snapping: {
      shouldSnapDragForNode: () => true,
      shouldSnapResizeForNode: () => true,
      defaultDragSnap: {width: 20, height: 20},
      defaultResizeSnap: {width: 20, height: 20},
    },
  };
    @ViewChild(ConnectWorkflowInspectorComponent)
    private inspector?: ConnectWorkflowInspectorComponent;

  ngOnInit(): void {
    this.loadProviders();

      this.sub.add(
          this.interactions.nodeFullscreenRequested$.subscribe(nodeId => this.openNodeInspectorFullscreen(nodeId)),
      );

    const isNewWorkflowRoute = this.route.snapshot.routeConfig?.path === 'connect/workflows/new';
    const initialId = this.route.snapshot.paramMap.get('id');
    this.setVisualizeRunId(this.route.snapshot.paramMap.get('runId'));
    if (initialId) {
      this.load(initialId);
    } else if (isNewWorkflowRoute) {
      this.initNewWorkflow();
    } else {
      this.error.set('No workflow selected.');
      this.loading.set(false);
      this.cdr.markForCheck();
    }

    this.sub.add(
      this.route.paramMap.subscribe(params => {
        const id = params.get('id');
        this.setVisualizeRunId(params.get('runId'));
        if (!id) return;
        if (id === this.lastRequestedWorkflowId) return;
        this.load(id);
      }),
    );
  }

    toggleWorkflowSettings(): void {
        this.workflowSettingsOpen.set(!this.workflowSettingsOpen());
    }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.loadSub?.unsubscribe();
    this.runSub?.unsubscribe();
    this.runContextSub?.unsubscribe();
    this.runReplaySub?.unsubscribe();
    this.diagramModel?.destroy();
  }

    closeWorkflowSettings(): void {
        this.workflowSettingsOpen.set(false);
    }

    addBackground(preset: BackgroundPreset): void {
        const def = BACKGROUND_PRESETS[preset];
        if (!def) return;

        const idx = this.backgrounds().length;
        const background: ConnectWorkflowCanvasBackground = {
            id: this.nextBackgroundId(),
            label: def.label,
            position: {x: 80 + idx * 40, y: 80 + idx * 40},
            size: {width: 560, height: 240},
            style: {...def.style},
        };

        this.backgrounds.set([...this.backgrounds(), background]);
        this.selectedBackgroundId.set(background.id);
        this.selectedNodeId.set(null);
        this.selectedEdgeId.set(null);
        this.diagramModel.updateNodes(nodes =>
            nodes.some(n => n.id === background.id) ? nodes : [...nodes, this.toDiagramBackgroundNode(background)],
        );
        this.selectionService.deselectAll();
        this.selectionService.select([background.id], []);
        this.cdr.markForCheck();
    }

  private loadProviders(): void {
    if (this.providersLoading()) return;
    this.providersLoading.set(true);
    this.sub.add(
      this.providersService
        .fetchCatalog()
        .pipe(
          take(1),
          catchError(() => {
            this.logClientFailure('Failed to load providers catalog.');
            return of(null);
          }),
          finalize(() => this.providersLoading.set(false)),
        )
        .subscribe(catalog => {
          this.providers.set(Array.isArray(catalog?.providers) ? catalog!.providers : []);
          this.applyProviderVisualsToDiagram();
          this.cdr.markForCheck();
        }),
    );
  }

  private applyProviderVisualsToDiagram(): void {
    const providerMeta = this.providerMeta();
    this.diagramModel.updateNodes(nodes =>
      nodes.map(n => {
        const data = (n.data ?? {}) as ConnectDiagramNodeData;
        const providerId = typeof data.providerId === 'string' && data.providerId.trim() ? data.providerId.trim() : providerFromRef(data.ref);
        const provider = providerId ? providerMeta.get(providerId) : undefined;
        if (!provider) return n;

        const nextData = {...data};
        let dirty = false;

        if (provider.id && nextData.providerId !== provider.id) {
          nextData.providerId = provider.id;
          dirty = true;
        }
        if (provider.logo && nextData.providerLogo !== provider.logo) {
          nextData.providerLogo = provider.logo;
          dirty = true;
        }
        if (provider.name && nextData.providerName !== provider.name) {
          nextData.providerName = provider.name;
          dirty = true;
        }
        if (provider.palette) {
          const currPalette = nextData.providerPalette || {};
          if (JSON.stringify(currPalette) !== JSON.stringify(provider.palette)) {
            nextData.providerPalette = provider.palette;
            dirty = true;
          }
        }

        if (!dirty) return n;
        return {...n, data: nextData};
      }),
    );
  }

  load(id: string): void {
    const workflowId = id.trim();
    this.loadSub?.unsubscribe();
    this.loadSub = null;

    if (!workflowId) {
      this.zone.run(() => {
        this.error.set('No workflow selected.');
        this.loading.set(false);
        this.cdr.markForCheck();
      });
      return;
    }

    this.lastRequestedWorkflowId = workflowId;
    this.zone.run(() => {
      this.error.set(null);
      this.loading.set(true);
      this.cdr.markForCheck();
    });
    this.loadSub = this.workflowsService
      .getWorkflow(workflowId)
      .pipe(
        timeout({first: 60_000}),
        take(1),
        catchError(err => {
          this.logClientFailure('Failed to load workflow.');
          this.zone.run(() => {
            this.error.set((err as {name?: string} | null)?.name === 'TimeoutError' ? 'Workflow load timed out.' : 'Could not load workflow.');
            this.cdr.markForCheck();
          });
          return of(null);
        }),
        finalize(() => {
          this.zone.run(() => {
            this.loading.set(false);
            this.cdr.markForCheck();
          });
        }),
      )
      .subscribe(wf => {
        if (!wf) return;
        this.zone.run(() => {
          this.workflow.set(wf);
          this.nodes.set(this.hydrateNodes(Array.isArray(wf.nodes) ? wf.nodes : []));
          this.edges.set(Array.isArray(wf.edges) ? [...wf.edges] : []);
            this.backgrounds.set(this.hydrateBackgrounds(Array.isArray(wf['x-ui']?.backgrounds) ? wf['x-ui']!.backgrounds! : []));
            this.selectedBackgroundId.set(null);
          this.syncDiagramModel();
          this.applyVisualFixtureSelection();
          this.loading.set(false);
          this.cdr.markForCheck();
        });
      });
  }

    addTag(event: MatChipInputEvent): void {
        const input = event.input;
        const raw = String(event.value ?? '');
        const normalized = this.normalizeTag(raw);

        if (input) {
            input.value = '';
        }

        if (!normalized) return;

        const wf = this.workflow();
        if (!wf) return;

        const existing = Array.isArray(wf.tags) ? wf.tags : [];
        if (existing.includes(normalized)) return;

        const next = [...existing, normalized].slice(0, 20);
        this.workflow.set({...wf, tags: next});
        this.cdr.markForCheck();
    }

    updateWorkflowName(value: string): void {
        const wf = this.workflow();
        if (!wf) return;
        const next = String(value ?? '');
        if (wf.name === next) return;
        this.workflow.set({...wf, name: next});
        this.cdr.markForCheck();
    }

    removeTag(tag: string): void {
        const wf = this.workflow();
        if (!wf) return;

        const normalized = this.normalizeTag(tag);
        if (!normalized) return;

        const existing = Array.isArray(wf.tags) ? wf.tags : [];
        const next = existing.filter(t => t !== normalized);
        if (next.length === existing.length) return;

        this.workflow.set({...wf, tags: next});
        this.cdr.markForCheck();
    }

    selectBackground(id: string): void {
        const bgId = String(id || '').trim();
        if (!bgId) return;
        this.selectedBackgroundId.set(bgId);
        this.selectedNodeId.set(null);
        this.selectedEdgeId.set(null);
        this.selectionService.deselectAll();
        this.selectionService.select([bgId], []);
        this.cdr.markForCheck();
    }

  onPaletteItemDropped(event: PaletteItemDroppedEvent): void {
    const diagramNode = event.node as SimpleNode<Record<string, unknown>>;
    const data = (diagramNode.data ?? {}) as Record<string, unknown>;

      if (diagramNode.type === 'connect.background') {
          const rawLabel = typeof data['label'] === 'string' ? String(data['label']).trim() : '';
          const label = rawLabel || 'Section';

          const position = (diagramNode.position as Point) ?? event.dropPosition;
          const rawSize = diagramNode.size as { width?: unknown; height?: unknown } | undefined;
          const size = {
              width: typeof rawSize?.width === 'number' && isFinite(rawSize.width) ? Math.max(1, rawSize.width) : 560,
              height: typeof rawSize?.height === 'number' && isFinite(rawSize.height) ? Math.max(1, rawSize.height) : 240,
          };

          const style: ConnectWorkflowCanvasBackgroundStyle = {};
          if (typeof data['fill'] === 'string' && String(data['fill']).trim()) style.fill = String(data['fill']).trim();
          if (typeof data['stroke'] === 'string' && String(data['stroke']).trim()) style.stroke = String(data['stroke']).trim();
          if (typeof data['opacity'] === 'number' && isFinite(data['opacity'] as number)) style.opacity = Math.min(Math.max(data['opacity'] as number, 0), 1);
          if (typeof data['radius'] === 'number' && isFinite(data['radius'] as number)) style.radius = Math.max(0, data['radius'] as number);

          const nextBackground: ConnectWorkflowCanvasBackground = {
              id: diagramNode.id,
              label,
              position,
              size,
              style: Object.keys(style).length ? style : undefined,
              locked: data['locked'] === true,
          };

          const existing = this.backgrounds();
          const idx = existing.findIndex(bg => bg.id === nextBackground.id);
          if (idx >= 0) {
              const updated = [...existing];
              updated[idx] = {...updated[idx], ...nextBackground};
              this.backgrounds.set(updated);
          } else {
              this.backgrounds.set([...existing, nextBackground]);
          }

          this.selectedBackgroundId.set(nextBackground.id);
          this.selectedNodeId.set(null);
          this.selectedEdgeId.set(null);

          this.diagramModel.updateNodes(nodes =>
              nodes.some(n => n.id === nextBackground.id) ? nodes : [...nodes, this.toDiagramBackgroundNode(nextBackground)],
          );
          this.updateDiagramBackgroundNode(nextBackground);
          this.selectionService.deselectAll();
          this.selectionService.select([nextBackground.id], []);
          this.cdr.markForCheck();
          return;
      }

    const workflowType = typeof data['type'] === 'string' ? (data['type'] as string).trim() : '';
    if (!workflowType) {
      return;
    }

    const label = typeof data['label'] === 'string' && (data['label'] as string).trim() ? (data['label'] as string).trim() : workflowType;
    const ref = typeof data['ref'] === 'string' && (data['ref'] as string).trim() ? (data['ref'] as string).trim() : workflowType;
    const allowedFields = [
      'input',
      'condition',
      'wait',
      'loop',
      'transform',
      'join',
      'objectType',
      'connection',
      'x-ai',
      'x-cache',
      'on_error',
      'retries',
      'timeout_ms',
      'idempotency_key',
    ] as const;
    const extra: Record<string, unknown> = {};
    allowedFields.forEach(key => {
      if (typeof data[key] === 'undefined') return;
      extra[key] = data[key];
    });

    const nextNode: ConnectWorkflowNode = {
      ...extra,
      id: diagramNode.id,
      type: workflowType,
      label,
      ref,
      ['x-ui']: {position: diagramNode.position as Point},
    };

    const workflowId = this.workflow()?.id;
    if (workflowId) {
      this.diagramModel.updateNodes(nodes =>
        nodes.map(n => (n.id === diagramNode.id ? {...n, data: {...(n.data as any), workflowId}} : n)),
      );
    }

    const existingIdx = this.nodes().findIndex(n => n.id === nextNode.id);
    if (existingIdx >= 0) {
      const updated = [...this.nodes()];
      updated[existingIdx] = {...updated[existingIdx], ...nextNode, ['x-ui']: {position: (nextNode['x-ui']?.position as Point) ?? {x: 0, y: 0}}};
      this.nodes.set(updated);
      return;
    }

    this.nodes.set([...this.nodes(), nextNode]);
  }

    deleteBackground(id: string): void {
        const bgId = String(id || '').trim();
        if (!bgId) return;

        this.backgrounds.set(this.backgrounds().filter(bg => bg.id !== bgId));
        if (this.selectedBackgroundId() === bgId) {
            this.selectedBackgroundId.set(null);
        }

        this.diagramModel.updateNodes(nodes => nodes.filter(n => n.id !== bgId));
        this.cdr.markForCheck();
    }

    patchBackground(id: string, patch: Partial<ConnectWorkflowCanvasBackground>): void {
        const bgId = String(id || '').trim();
        if (!bgId) return;

        const current = this.backgrounds();
        const idx = current.findIndex(bg => bg.id === bgId);
        if (idx < 0) return;

        const next = [...current];
        next[idx] = {...next[idx], ...patch};
        this.backgrounds.set(next);
        this.updateDiagramBackgroundNode(next[idx]);
        this.cdr.markForCheck();
    }

    patchBackgroundStyle(id: string, patch: Partial<ConnectWorkflowCanvasBackgroundStyle>): void {
        const bgId = String(id || '').trim();
        if (!bgId) return;

        const current = this.backgrounds();
        const idx = current.findIndex(bg => bg.id === bgId);
        if (idx < 0) return;

        const prev = current[idx];
        const merged: ConnectWorkflowCanvasBackgroundStyle = {...(prev.style ?? {}), ...patch};
        const nextStyle: ConnectWorkflowCanvasBackgroundStyle = {};

        const fill = typeof merged.fill === 'string' && merged.fill.trim() ? merged.fill.trim() : undefined;
        if (fill) nextStyle.fill = fill;

        const stroke = typeof merged.stroke === 'string' && merged.stroke.trim() ? merged.stroke.trim() : undefined;
        if (stroke) nextStyle.stroke = stroke;

        const opacity = typeof merged.opacity === 'number' && isFinite(merged.opacity) ? Math.min(Math.max(merged.opacity, 0), 1) : undefined;
        if (typeof opacity === 'number') nextStyle.opacity = opacity;

        const radius = typeof merged.radius === 'number' && isFinite(merged.radius) ? Math.max(0, merged.radius) : undefined;
        if (typeof radius === 'number') nextStyle.radius = radius;

        const next = [...current];
        next[idx] = {...prev, style: Object.keys(nextStyle).length ? nextStyle : undefined};
        this.backgrounds.set(next);
        this.updateDiagramBackgroundNode(next[idx]);
        this.cdr.markForCheck();
    }

  save(): void {
    const wf = this.workflow();
    if (!wf) return;

    const name = String(wf.name || '').trim();
    if (!name) {
      this.zone.run(() => {
        this.error.set('Workflow name is required.');
        this.cdr.markForCheck();
      });
      return;
    }

    const workflowId = wf.id;
    this.zone.run(() => {
      this.error.set(null);
      this.saving.set(true);
      this.cdr.markForCheck();
    });

      const payload: ConnectWorkflow = {
          ...wf,
          name,
          nodes: this.nodes(),
          edges: this.edges(),
          ['x-ui']: {
              ...(wf['x-ui'] ?? {}),
              backgrounds: this.backgrounds(),
          },
      };
    const request$ = workflowId ? this.workflowsService.updateWorkflow(workflowId, payload) : this.workflowsService.createWorkflow(payload);
    request$
      .pipe(
        take(1),
        catchError(() => {
          this.logClientFailure('Failed to save workflow.');
          this.zone.run(() => {
            this.error.set('Could not save workflow.');
            this.cdr.markForCheck();
          });
          return of(null);
        }),
        finalize(() => {
          this.zone.run(() => {
            this.saving.set(false);
            this.cdr.markForCheck();
          });
        }),
      )
      .subscribe(updated => {
        if (!updated) return;
        this.zone.run(() => {
          this.workflow.set(updated);
          this.syncDiagramModel();
          this.cdr.markForCheck();
        });

        if (!workflowId && updated.id) {
          this.lastRequestedWorkflowId = updated.id;
          this.router.navigate(['/connect/workflows', updated.id, 'edit'], {replaceUrl: true});
        }
      });
  }

  // Diagram events
  onSelectionChanged(event: SelectionChangedEvent): void {
      const selectedNodes = event.selectedNodes ?? [];
      const selectedEdges = event.selectedEdges ?? [];

      const workflowNode = selectedNodes.find(n => n.type !== 'connect.background');
      const backgroundNode = selectedNodes.find(n => n.type === 'connect.background');

      if (workflowNode?.id) {
          this.selectedNodeId.set(workflowNode.id);
          this.selectedBackgroundId.set(null);
          this.selectedEdgeId.set(null);
          return;
      }

      if (backgroundNode?.id) {
          this.selectedBackgroundId.set(backgroundNode.id);
          this.selectedNodeId.set(null);
          this.selectedEdgeId.set(null);
          return;
      }

      this.selectedNodeId.set(null);
      this.selectedBackgroundId.set(null);
      this.selectedEdgeId.set(selectedEdges[0]?.id || null);
  }

    onInspectorBackgroundPatched(event: {
        backgroundId: string;
        patch: Partial<ConnectWorkflowCanvasBackground>
    }): void {
        this.patchBackground(event.backgroundId, event.patch);
    }

  onSelectionMoved(event: SelectionMovedEvent): void {
    const positions = new Map(event.nodes.map(n => [n.id, n.position]));
    this.nodes.set(
      this.nodes().map(n => {
        const nextPos = positions.get(n.id);
        if (!nextPos) return n;
        return {...n, ['x-ui']: {position: nextPos}};
      }),
    );

      this.backgrounds.set(
          this.backgrounds().map(bg => {
              const nextPos = positions.get(bg.id);
              if (!nextPos) return bg;
              return {...bg, position: nextPos};
          }),
      );
  }

  onSelectionRemoved(event: SelectionRemovedEvent): void {
    const deletedNodeIds = new Set(event.deletedNodes.map(n => n.id));
    const deletedEdgeIds = new Set(event.deletedEdges.map(e => e.id || ''));
    const deletedEdgeKeys = new Set(event.deletedEdges.map(e => this.edgeKeyFromDiagramEdge(e)));
    this.nodes.set(this.nodes().filter(n => !deletedNodeIds.has(n.id)));
      this.backgrounds.set(this.backgrounds().filter(bg => !deletedNodeIds.has(bg.id)));
    this.edges.set(
      this.edges().filter(e => {
        if (e.id && deletedEdgeIds.has(e.id)) return false;
        if (deletedEdgeKeys.has(this.edgeKeyFromConnectEdge(e))) return false;
        return !deletedNodeIds.has(e.source) && !deletedNodeIds.has(e.target);
      }),
    );

    if (this.selectedNodeId() && deletedNodeIds.has(this.selectedNodeId()!)) {
      this.selectedNodeId.set(null);
    }

    if (this.selectedEdgeId() && deletedEdgeIds.has(this.selectedEdgeId()!)) {
      this.selectedEdgeId.set(null);
    }

      if (this.selectedBackgroundId() && deletedNodeIds.has(this.selectedBackgroundId()!)) {
          this.selectedBackgroundId.set(null);
      }

    if (this.diagramModel) {
      this.diagramModel.updateNodes(nodes => nodes.filter(n => !deletedNodeIds.has(n.id)));
      this.diagramModel.updateEdges(edges =>
        edges.filter(e => {
          if (e.id && deletedEdgeIds.has(e.id)) return false;
          if (deletedEdgeKeys.has(this.edgeKeyFromDiagramEdge(e))) return false;
          return !deletedNodeIds.has(e.source) && !deletedNodeIds.has(e.target);
        }),
      );
    }
  }

    onNodeResized(event: NodeResizedEvent): void {
        const node = event.node as SimpleNode<Record<string, unknown>> | undefined;
        if (!node || node.type !== 'connect.background') return;
        const size = node.size;
        if (!size) return;

        this.backgrounds.set(
            this.backgrounds().map(bg => (bg.id === node.id ? {
                ...bg,
                size: {width: size.width, height: size.height}
            } : bg)),
        );
    }

  private initNewWorkflow(): void {
    this.lastRequestedWorkflowId = null;
    this.loadSub?.unsubscribe();
    this.loadSub = null;
    this.setVisualizeRunId(null);
    this.zone.run(() => {
      this.error.set(null);
      this.loading.set(false);
      this.selectedNodeId.set(null);
      this.selectedEdgeId.set(null);
        this.selectedBackgroundId.set(null);
        this.workflow.set({name: 'New workflow', nodes: [], edges: [], ['x-ui']: {backgrounds: []}});
      this.nodes.set([]);
      this.edges.set([]);
        this.backgrounds.set([]);
      this.syncDiagramModel();
      this.cdr.markForCheck();
    });
  }

    private normalizeTag(value: string): string | null {
        const trimmed = value.trim().toLowerCase();
        if (!trimmed) return null;
        if (trimmed.length > 64) return null;
        return trimmed;
    }

  onEdgeDrawn(event: {edge: Edge}): void {
    if (!this.diagramModel) {
      return;
    }

    const id = event.edge.id || `e-${Date.now()}`;
    const label = (event.edge.data as {label?: string} | undefined)?.label;
    const newEdge: ConnectWorkflowEdge = {
      id,
      source: event.edge.source,
      target: event.edge.target,
      source_handle: event.edge.sourcePort || 'out',
      target_handle: event.edge.targetPort || 'in',
      label,
    };
    this.edges.set([...this.edges(), newEdge]);

    const diagramEdge: Edge = {
      ...event.edge,
      id,
      sourcePort: event.edge.sourcePort || 'out',
      targetPort: event.edge.targetPort || 'in',
    };

    this.diagramModel.updateEdges(edges => (edges.some(e => e.id === id) ? edges : [...edges, diagramEdge]));
  }

  get selectedNode(): ConnectWorkflowNode | null {
    const id = this.selectedNodeId();
    if (!id) return null;
    return this.nodes().find(n => n.id === id) ?? null;
  }

  get selectedEdge(): ConnectWorkflowEdge | null {
    const id = this.selectedEdgeId();
    if (!id) return null;
    const direct = this.edges().find(e => e.id === id);
    if (direct) return direct;
    return this.edges().find(e => this.edgeKeyFromConnectEdge(e) === id) ?? null;
  }

  exitRunView(): void {
    const wfId = this.workflow()?.id;
    if (!wfId) return;
    this.router.navigate(['/connect/workflows', wfId, 'edit']);
  }

  private setVisualizeRunId(runId: string | null): void {
    const next = typeof runId === 'string' && runId.trim() ? runId.trim() : null;
    if (this.visualizeRunId() === next) return;
    this.visualizeRunId.set(next);

    this.runSub?.unsubscribe();
    this.runSub = null;
    this.runContextSub?.unsubscribe();
    this.runContextSub = null;
    this.runReplaySub?.unsubscribe();
    this.runReplaySub = null;
    this.visualizedRun.set(null);
    this.visualizedRunLoading.set(false);
    this.visualizedRunError.set(null);
    this.visualizedRunContext.set(null);
    this.visualizedRunContextLoading.set(false);
    this.visualizedRunContextError.set(null);
    this.visualizedRunReplay.set(null);
    this.visualizedRunReplayLoading.set(false);
    this.visualizedRunReplayError.set(null);

    if (!next) {
      this.applyRunToDiagram(null);
      return;
    }

    this.loadRunDebugArtifacts(next);
    this.visualizedRunLoading.set(true);
    this.runSub = timer(0, 2000)
      .pipe(
        switchMap(() =>
          this.workflowsService.getRun(next).pipe(
            timeout({first: 20_000}),
            catchError(() => {
              this.logClientFailure('Failed to load run.');
              this.visualizedRunError.set('Could not load run.');
              return of<ConnectRun | null>(null);
            }),
          ),
        ),
        take(60),
        takeWhile(run => !!run && !this.isFinalRunStatus(run.status), true),
        finalize(() => this.visualizedRunLoading.set(false)),
      )
      .subscribe(run => {
        if (!run) return;
        this.visualizedRun.set(run);
        this.applyRunToDiagram(run);
      });
  }

  private loadRunDebugArtifacts(runId: string): void {
    this.visualizedRunContextLoading.set(true);
    this.runContextSub = this.workflowsService
      .getRunContext(runId)
      .pipe(
        take(1),
        timeout({first: 20_000}),
        catchError(() => {
          this.logClientFailure('Failed to load run context.');
          this.visualizedRunContextError.set('Could not load runtime context.');
          return of<ConnectRunContext | null>(null);
        }),
        finalize(() => this.visualizedRunContextLoading.set(false)),
      )
      .subscribe(context => {
        if (!context) return;
        this.visualizedRunContext.set(context);
      });

    this.visualizedRunReplayLoading.set(true);
    this.runReplaySub = this.workflowsService
      .getRunReplay(runId)
      .pipe(
        take(1),
        timeout({first: 20_000}),
        catchError(() => {
          this.logClientFailure('Failed to load replay summary.');
          this.visualizedRunReplayError.set('Could not load replay summary.');
          return of<ConnectRunReplaySummary | null>(null);
        }),
        finalize(() => this.visualizedRunReplayLoading.set(false)),
      )
      .subscribe(replay => {
        if (!replay) return;
        this.visualizedRunReplay.set(replay);
      });
  }

  private applyRunToDiagram(run: ConnectRun | null): void {
    const statusByNodeId = new Map<string, string>();
    for (const step of run?.steps ?? []) {
      statusByNodeId.set(step.node_id, String(step.status || ''));
    }
    this.diagramModel.updateNodes(nodes =>
      nodes.map(n => ({...n, data: {...(n.data as any), status: statusByNodeId.get(n.id) || ''}})),
    );
  }

  private isFinalRunStatus(status: string | undefined | null): boolean {
    const s = String(status || '').toLowerCase();
    return s === 'succeeded' || s === 'failed' || s === 'cancelled' || s === 'timed_out';
  }

  private logClientFailure(message: string): void {
    console.error(message);
  }

  onInspectorNodePatched(event: {nodeId: string; patch: Partial<ConnectWorkflowNode>}): void {
    const currentNodes = this.nodes();
    const idx = currentNodes.findIndex(n => n.id === event.nodeId);
    if (idx < 0) return;
    const updated = [...currentNodes];
    updated[idx] = {...updated[idx], ...event.patch};
    this.nodes.set(updated);

    const next = updated[idx];
    const shouldRecomputeProvider = 'ref' in event.patch;
    const providerId = shouldRecomputeProvider ? providerFromRef(next.ref) : '';
    const provider = providerId ? this.providerMeta().get(providerId) : undefined;
    this.diagramModel.updateNodes(nodes =>
      nodes.map(n =>
        n.id === event.nodeId
          ? {
              ...n,
              data: {
                ...(n.data as ConnectDiagramNodeData),
                label: next.label || next.id,
                ref: next.ref,
                type: next.type,
                ...(shouldRecomputeProvider
                  ? {
                      providerId: provider?.id,
                      providerName: provider?.name,
                      providerLogo: provider?.logo,
                      providerPalette: provider?.palette,
                    }
                  : {}),
              },
            }
          : n,
      ),
    );
  }

  onInspectorEdgePatched(event: {edgeId: string; patch: Partial<ConnectWorkflowEdge>}): void {
    const updated = this.edges().map(e => {
      const id = e.id || this.edgeKeyFromConnectEdge(e);
      if (id !== event.edgeId) return e;
      return {...e, ...event.patch};
    });
    this.edges.set(updated);

    this.diagramModel.updateEdges(edges =>
      edges.map(e =>
        (e.id || this.edgeKeyFromDiagramEdge(e)) === event.edgeId
          ? {
              ...e,
              data: {
                ...(e.data as {label?: string} | undefined),
                label: (event.patch as {label?: string} | undefined)?.label ?? (e.data as {label?: string} | undefined)?.label,
              },
            }
          : e,
      ),
    );
  }

    onInspectorBackgroundStylePatched(event: {
        backgroundId: string;
        patch: Partial<ConnectWorkflowCanvasBackgroundStyle>
    }): void {
        this.patchBackgroundStyle(event.backgroundId, event.patch);
    }

    onInspectorBackgroundDeleted(event: { backgroundId: string }): void {
        this.deleteBackground(event.backgroundId);
    }

    private openNodeInspectorFullscreen(nodeId: string): void {
        const id = String(nodeId || '').trim();
        if (!id) return;
        if (!this.nodes().some(n => n.id === id)) return;

        this.selectedNodeId.set(id);
        this.selectedBackgroundId.set(null);
        this.selectedEdgeId.set(null);
        this.cdr.markForCheck();

        const tryToggleFullscreen = (): boolean => {
            const inspector = this.inspector;
            if (!inspector || inspector.isFullscreen()) return false;
            inspector.toggleFullscreen();
            return true;
        };

        if (tryToggleFullscreen()) return;

        setTimeout(() => {
            this.zone.run(() => {
                tryToggleFullscreen();
            });
        });
    }

  private applyVisualFixtureSelection(): void {
    const nodeId = this.readVisualFixtureSelectedNodeId();
    if (!nodeId || !this.nodes().some(node => node.id === nodeId)) return;

    this.selectedNodeId.set(nodeId);
    this.selectedBackgroundId.set(null);
    this.selectedEdgeId.set(null);
  }

  private readVisualFixtureSelectedNodeId(): string | null {
    const requested = this.route.snapshot.queryParamMap.get('connectWorkflowFixtureNode')
      ?? this.route.snapshot.data['visualFixtureSelectedNodeId'];
    return typeof requested === 'string' && requested.trim() ? requested.trim() : null;
  }

  private syncDiagramModel(): void {
      this.diagramModel.updateNodes([...this.toDiagramBackgroundNodes(this.backgrounds()), ...this.toDiagramNodes(this.nodes())]);
    this.diagramModel.updateEdges(this.toDiagramEdges(this.edges()));
  }

    private nextBackgroundId(): string {
        return `bg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    }

    private updateDiagramBackgroundNode(background: ConnectWorkflowCanvasBackground): void {
        const nextNode = this.toDiagramBackgroundNode(background);
        this.diagramModel.updateNodes(nodes =>
            nodes.map(n => (n.id === background.id ? {...n, ...nextNode, selected: n.selected} : n)),
        );
    }

    private toDiagramBackgroundNodes(backgrounds: ConnectWorkflowCanvasBackground[]): SimpleNode<ConnectDiagramBackgroundData>[] {
        return backgrounds.map(bg => this.toDiagramBackgroundNode(bg));
    }

    private toDiagramBackgroundNode(background: ConnectWorkflowCanvasBackground): SimpleNode<ConnectDiagramBackgroundData> {
        const style = background.style ?? {};
        const locked = background.locked === true;

        return {
            id: background.id,
            position: background.position,
            size: background.size,
            autoSize: false,
            resizable: !locked,
            rotatable: false,
            zOrder: -1000,
            data: {
                label: background.label,
                fill: style.fill,
                stroke: style.stroke,
                opacity: style.opacity,
                radius: style.radius,
                locked,
            },
            type: 'connect.background',
        };
    }

  private toDiagramNodes(nodes: ConnectWorkflowNode[]): SimpleNode<ConnectDiagramNodeData>[] {
    const workflowId = this.workflow()?.id;
    const statusByNodeId = new Map<string, string>();
    for (const step of this.visualizedRun()?.steps ?? []) {
      statusByNodeId.set(step.node_id, String(step.status || ''));
    }

    const providerMeta = this.providerMeta();

    return nodes.map(n => {
      const providerId = providerFromRef(n.ref);
      const provider = providerId ? providerMeta.get(providerId) : undefined;
      return {
        id: n.id,
        position: n['x-ui']?.position || {x: 0, y: 0},
        data: {
          label: n.label || n.id,
          ref: n.ref,
          type: n.type,
          status: statusByNodeId.get(n.id) || '',
          workflowId,
          providerId: provider?.id,
          providerName: provider?.name,
          providerLogo: provider?.logo,
          providerPalette: provider?.palette,
        },
        type: 'connect.default',
      };
    });
  }

  private toDiagramEdges(edges: ConnectWorkflowEdge[]): Edge[] {
    return edges.map(e => ({
      id: e.id || `${e.source}:${e.source_handle || 'out'}->${e.target}:${e.target_handle || 'in'}`,
      source: e.source,
      target: e.target,
      sourcePort: e.source_handle || 'out',
      targetPort: e.target_handle || 'in',
      data: {label: e.label},
    }));
  }

  private edgeKeyFromConnectEdge(edge: ConnectWorkflowEdge): string {
    const sourcePort = edge.source_handle || 'out';
    const targetPort = edge.target_handle || 'in';
    return `${edge.source}:${sourcePort}->${edge.target}:${targetPort}`;
  }

  private edgeKeyFromDiagramEdge(edge: Edge): string {
    const sourcePort = edge.sourcePort || 'out';
    const targetPort = edge.targetPort || 'in';
    return `${edge.source}:${sourcePort}->${edge.target}:${targetPort}`;
  }

  private hydrateNodes(nodes: ConnectWorkflowNode[]): ConnectWorkflowNode[] {
    return nodes.map((n, idx) => {
      const pos = n['x-ui']?.position ?? this.generatePosition(idx);
      return {...n, ['x-ui']: {position: pos}};
    });
  }

    private hydrateBackgrounds(backgrounds: ConnectWorkflowCanvasBackground[]): ConnectWorkflowCanvasBackground[] {
        const list = Array.isArray(backgrounds) ? backgrounds : [];

        const hydrated: ConnectWorkflowCanvasBackground[] = [];
        for (const [idx, bg] of list.entries()) {
            const id = typeof (bg as any)?.id === 'string' ? String((bg as any).id).trim() : '';
            if (!id) continue;

            const rawPos = (bg as any)?.position as { x?: unknown; y?: unknown } | undefined;
            const position = {
                x: typeof rawPos?.x === 'number' && isFinite(rawPos.x) ? rawPos.x : 80 + idx * 40,
                y: typeof rawPos?.y === 'number' && isFinite(rawPos.y) ? rawPos.y : 80 + idx * 40,
            };

            const rawSize = (bg as any)?.size as { width?: unknown; height?: unknown } | undefined;
            const size = {
                width: typeof rawSize?.width === 'number' && isFinite(rawSize.width) ? Math.max(1, rawSize.width) : 560,
                height: typeof rawSize?.height === 'number' && isFinite(rawSize.height) ? Math.max(1, rawSize.height) : 240,
            };

            const background: ConnectWorkflowCanvasBackground = {id, position, size};

            if (typeof (bg as any)?.label === 'string' && String((bg as any).label).trim()) {
                background.label = String((bg as any).label);
            }

            if (typeof (bg as any)?.locked === 'boolean') {
                background.locked = Boolean((bg as any).locked);
            }

            const rawStyle = (bg as any)?.style as Record<string, unknown> | undefined;
            if (rawStyle && typeof rawStyle === 'object') {
                const style: ConnectWorkflowCanvasBackgroundStyle = {};
                if (typeof rawStyle['fill'] === 'string' && String(rawStyle['fill']).trim()) style.fill = String(rawStyle['fill']).trim();
                if (typeof rawStyle['stroke'] === 'string' && String(rawStyle['stroke']).trim()) style.stroke = String(rawStyle['stroke']).trim();

                const opacity = rawStyle['opacity'];
                if (typeof opacity === 'number' && isFinite(opacity)) style.opacity = Math.min(Math.max(opacity, 0), 1);

                const radius = rawStyle['radius'];
                if (typeof radius === 'number' && isFinite(radius)) style.radius = Math.max(0, radius);

                if (Object.keys(style).length) {
                    background.style = style;
                }
            }

            hydrated.push(background);
        }

        return hydrated;
    }

  private generatePosition(index: number = this.nodes().length): {x: number; y: number} {
    const spacing = 180;
    const x = 80 + (index % 4) * spacing;
    const y = 80 + Math.floor(index / 4) * spacing;
    return {x, y};
  }
}
